// 016 — Atención Inmediata al Recién Nacido (fila 14 de 8.3-bis + Fase 5,
// Tanda 2: los 15 bloques oficiales A-O, ver `bloques016Datos.ts`).
//
// FHIR (solo lo que ya tenía consumidor real, mismo criterio que antes):
// `Procedure` (atención al nacer), `Observation` con LOINC para el Apgar
// total al minuto 1/5/10 (ahora calculado desde la grilla, ya no tipeado) y
// peso/talla/perímetro cefálico INICIALES, y un `Condition` por cada
// diagnóstico inicial con texto. Nota honesta: los LOINC de Apgar
// (9271-8/9274-2/9273-4) se usan por ser los de uso más extendido — se
// verifican formalmente cuando se cargue el catálogo real (Fase 9). Todo el
// resto (antecedentes, examen físico por sistema, egreso...) queda como
// `Par[]` de respaldo en el PDF firmado.
import type { Observation } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps, Par } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { Bloques016 } from './Bloques016';
import { estadoInicialBitacora016, paresDeBitacora016, totalApgar016, type MinutoApgar016 } from './bloques016Datos';
import { codeDeDiagnostico } from './SelectorDiagnosticoCie10';
import { VITAL_SIGNS_CATEGORY } from './vitalSigns';
import { reasonReferenceDeCondicion } from '../recetas/reasonReference';

const CODIGO = '016';

const APGAR_LOINC: readonly { readonly minuto: MinutoApgar016; readonly loinc: string; readonly display: string }[] = [
  { minuto: '1', loinc: '9271-8', display: 'Apgar al minuto 1' },
  { minuto: '5', loinc: '9274-2', display: 'Apgar al minuto 5' },
  { minuto: '10', loinc: '9273-4', display: 'Apgar al minuto 10' },
];

const ANTROPOMETRIA_LOINC = [
  { clave: 'inicialPeso', loinc: '29463-7', display: 'Peso al nacer', unidad: 'kg', unidadUcum: 'kg' },
  { clave: 'inicialLongitud', loinc: '8302-2', display: 'Talla al nacer', unidad: 'cm', unidadUcum: 'cm' },
  { clave: 'inicialPerimetroCefalico', loinc: '9843-4', display: 'Perímetro cefálico al nacer', unidad: 'cm', unidadUcum: 'cm' },
] as const;

function numeroOIndefinido(texto: string | undefined): number | undefined {
  const valor = texto === undefined || texto.trim() === '' ? undefined : Number(texto);
  return valor !== undefined && !Number.isNaN(valor) ? valor : undefined;
}

export function Formulario016({ subject, encounter, conditionId, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('hub.formulario.016');
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): Recién nacido (edad en horas): mismo criterio que 016A/016B/016C, Neonatología.
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo, { servicio: 'Neonatología' });

  const [bitacora, setBitacora] = useState(() => estadoInicialBitacora016());

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const lote = new LoteTransaccional();
      const pares: Par[] = paresDeBitacora016(t, bitacora);
      const ahora = new Date().toISOString();

      lote.crear({
        resourceType: 'Procedure',
        status: 'completed',
        code: { text: 'Atención inmediata al recién nacido' },
        subject,
        encounter,
        performedDateTime: ahora,
        reasonReference: reasonReferenceDeCondicion(conditionId),
      });

      const observaciones: Observation[] = [];
      const crear = (loinc: string, display: string, value: number, unidad: string, unidadUcum: string): void => {
        observaciones.push(
          lote.crear<Observation>({
            resourceType: 'Observation',
            status: 'final',
            category: [VITAL_SIGNS_CATEGORY],
            code: { coding: [{ system: 'http://loinc.org', code: loinc, display }] },
            subject,
            encounter,
            effectiveDateTime: ahora,
            valueQuantity: { value, unit: unidad, system: 'http://unitsofmeasure.org', code: unidadUcum },
          }),
        );
      };
      for (const a of APGAR_LOINC) {
        const total = totalApgar016(bitacora.apgar, a.minuto);
        if (total !== undefined) crear(a.loinc, a.display, total, '{score}', 'score');
      }
      for (const m of ANTROPOMETRIA_LOINC) {
        const valor = numeroOIndefinido(bitacora.antropometria[m.clave]);
        if (valor !== undefined) crear(m.loinc, m.display, valor, m.unidad, m.unidadUcum);
      }

      for (const d of bitacora.diagnosticosInicial) {
        if (d.diagnostico.texto.trim() !== '') {
          lote.crear({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(d.diagnostico), recordedDate: ahora });
        }
      }

      await lote.enviar(medplum);
      onGuardado(pares, datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloques016 estado={bitacora} onChange={setBitacora} />

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
